import { useState } from 'react';
import type { ActivitySubjectCard } from '@shared/types/activity-feed';
import type { ActivitySortMode } from './ActivitySubjectTableControls';
import {
  ActivitySubjectTableRow,
  getActivitySubjectId,
} from './ActivitySubjectTableRow';

interface ActivitySubjectTableProps {
  subjects: ActivitySubjectCard[];
  archivingSubjectKey: string | null;
  sortMode: ActivitySortMode;
  hasActiveFilters: boolean;
  onSortModeChange: (value: ActivitySortMode) => void;
  onResetFilters: () => void;
  onOpen: (subject: ActivitySubjectCard) => void;
  onArchive: (subject: ActivitySubjectCard) => void;
}

export const ActivitySubjectTable = ({
  subjects,
  archivingSubjectKey,
  sortMode,
  hasActiveFilters,
  onSortModeChange,
  onResetFilters,
  onOpen,
  onArchive,
}: ActivitySubjectTableProps) => {
  const [openMenuSubjectId, setOpenMenuSubjectId] = useState<string | null>(null);

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <select
          aria-label="Trier les comptes"
          value={sortMode}
          onChange={(event) => onSortModeChange(event.target.value as ActivitySortMode)}
          className="h-9 rounded-lg border border-domino-cardBorder bg-domino-white px-3 text-sm text-domino-ink outline-none transition focus:border-domino-prune focus:ring-2 focus:ring-domino-prune/10"
        >
          <option value="recent">Activité récente</option>
          <option value="priority">Priorité</option>
          <option value="az">A–Z</option>
        </select>
      </div>
      <div className="overflow-visible border-y border-domino-cardBorder">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-[46%]" />
            <col className="w-[44%]" />
            <col className="w-[10%]" />
          </colgroup>
          <thead className="border-b border-domino-cardBorder">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-domino-inkSoft sm:px-4">
                Compte
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-domino-inkSoft sm:px-4">
                Prochaine action
              </th>
              <th><span className="sr-only">Options</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-domino-cardBorder">
            {subjects.length === 0 && (
              <tr>
                <td colSpan={3} className="px-5 py-16 text-center">
                  <div className="mx-auto flex max-w-md flex-col items-center">
                    <p className="text-base font-semibold text-domino-ink">
                      Aucun compte
                    </p>
                    <p className="mt-2 text-sm leading-6 text-domino-inkSoft">
                      Ajustez la recherche ou les filtres.
                    </p>
                    {hasActiveFilters && (
                      <button
                        type="button"
                        onClick={onResetFilters}
                        className="mt-4 inline-flex h-10 items-center justify-center rounded-xl border border-domino-cardBorder bg-domino-white px-4 text-sm font-semibold text-domino-aubergine transition hover:border-domino-prune hover:bg-domino-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune focus-visible:ring-offset-2 focus-visible:ring-offset-domino-warm"
                      >
                        Réinitialiser les filtres
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {subjects.map((subject) => {
              const subjectId = getActivitySubjectId(subject);

              return (
                <ActivitySubjectTableRow
                  key={subjectId}
                  subject={subject}
                  isMenuOpen={openMenuSubjectId === subjectId}
                  isArchiving={archivingSubjectKey === subjectId}
                  onOpen={onOpen}
                  onArchive={onArchive}
                  onToggleMenu={() => setOpenMenuSubjectId((currentId) => (
                    currentId === subjectId ? null : subjectId
                  ))}
                  onCloseMenu={() => setOpenMenuSubjectId(null)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
